import React, { useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Snackbar,
  FormControlLabel,
  Checkbox,
  Chip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  FileDownload as ExportIcon,
  FileUpload as ImportIcon,
  TravelExplore as WebSearchIcon,
} from '@mui/icons-material';
import { adressenApi } from '../services/api';
import { authService } from '../services/authService';
import type { Adresse, CreateAdresseDto } from '../types/api';
import { getApiBaseUrl } from '../utils/apiUrl';
import AddressSearchAutocomplete from '../components/AddressSearchAutocomplete';
import AdresseDetailDialog from '../components/AdresseDetailDialog';

const Adressen: React.FC = () => {
  const [adressen, setAdressen] = useState<Adresse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAdresse, setEditingAdresse] = useState<Adresse | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ imported: number; errors: number; errorDetails?: string[] } | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'info' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedAdresseId, setSelectedAdresseId] = useState<number | null>(null);
  const [formData, setFormData] = useState<CreateAdresseDto>({
    anrede: '',
    titel: '',
    vorname: '',
    nachname: '',
    zusatzName: '',
    firma: '',
    email: '',
    telefon: '',
    strasse: '',
    hausnummer: '',
    zusatzadresse: '',
    plz: '',
    postfach: '',
    ort: '',
    land: 'Schweiz',
    istMandant: false,
    mandantSeit: undefined,
    mandantNummer: '',
  });

  useEffect(() => {
    loadAdressen();
  }, []);

  const loadAdressen = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adressenApi.getAll({ take: 500 });
      setAdressen(response.data);
    } catch (err: any) {
      console.error('Adressen-Fehler:', err);
      setError(err.response?.data?.message || 'Fehler beim Laden der Adressen');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (adresse?: Adresse) => {
    if (adresse) {
      setEditingAdresse(adresse);
      setFormData({
        anrede: adresse.anrede || '',
        titel: adresse.titel || '',
        vorname: adresse.vorname || '',
        nachname: adresse.nachname || '',
        zusatzName: adresse.zusatzName || '',
        firma: adresse.firma || '',
        email: adresse.email || '',
        telefon: adresse.telefon || '',
        strasse: adresse.strasse || '',
        hausnummer: adresse.hausnummer || '',
        zusatzadresse: adresse.zusatzadresse || '',
        plz: adresse.plz || '',
        postfach: adresse.postfach || '',
        ort: adresse.ort || '',
        land: adresse.land || 'Deutschland',
        istMandant: adresse.istMandant || false,
        mandantSeit: adresse.mandantSeit || undefined,
        mandantNummer: adresse.mandantNummer || '',
      });
    } else {
      setEditingAdresse(null);
      setFormData({
        anrede: '',
        titel: '',
        vorname: '',
        nachname: '',
        zusatzName: '',
        firma: '',
        email: '',
        telefon: '',
        strasse: '',
        hausnummer: '',
        zusatzadresse: '',
        plz: '',
        postfach: '',
        ort: '',
        land: 'Schweiz',
        istMandant: false,
        mandantSeit: undefined,
        mandantNummer: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingAdresse(null);
  };

  const handleSave = async () => {
    try {
      if (editingAdresse) {
        const response = await adressenApi.update(
          editingAdresse.adresseID,
          formData
        );
        setAdressen(
          adressen.map((a) =>
            a.adresseID === editingAdresse.adresseID ? response.data : a
          )
        );
      } else {
        const response = await adressenApi.create(formData);
        setAdressen([...adressen, response.data]);
      }
      handleCloseDialog();
    } catch (err: any) {
      alert('Fehler beim Speichern der Adresse');
    }
  };

  const handleDelete = async (adresseId: number) => {
    if (!window.confirm('Adresse wirklich löschen?')) return;

    try {
      await adressenApi.delete(adresseId);
      setAdressen(adressen.filter((a) => a.adresseID !== adresseId));
    } catch (err: any) {
      alert('Fehler beim Löschen der Adresse');
    }
  };

  const handleExport = async () => {
    try {
      const token = authService.getToken();
      const apiUrl = getApiBaseUrl();
      const response = await fetch(`${apiUrl}/api/adressen/export`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Export fehlgeschlagen' }));
        console.error('Export Fehler:', errorData);
        console.error('Export Errors Detail:', errorData.errors);
        throw new Error(errorData.message || errorData.title || 'Export fehlgeschlagen');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Adressen_Export_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Export Exception:', err);
      alert(`Fehler beim CSV-Export: ${err.message}`);
      setError('Fehler beim CSV-Export');
    }
  };

  const handleOpenImportDialog = () => {
    setImportDialogOpen(true);
    setImportFile(null);
    setImportResult(null);
  };

  const handleCloseImportDialog = () => {
    setImportDialogOpen(false);
    setImportFile(null);
    setImportResult(null);
  };

  const handleImport = async () => {
    if (!importFile) {
      alert('Bitte wählen Sie eine CSV-Datei aus');
      return;
    }

    try {
      setImporting(true);
      const token = authService.getToken();
      const formData = new FormData();
      formData.append('file', importFile);

      const apiUrl = getApiBaseUrl();
      const response = await fetch(`${apiUrl}/api/adressen/import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Import fehlgeschlagen');
      }

      const result = await response.json();
      setImportResult(result);

      // Reload addresses to show imported data
      if (result.imported > 0) {
        await loadAdressen();
      }
    } catch (err: any) {
      alert('Fehler beim CSV-Import');
    } finally {
      setImporting(false);
    }
  };

  // Handler für Adressauswahl aus der intelligenten Suche
  const handleAddressSelected = (adresse: Adresse) => {
    // Prüfen ob Adresse bereits in der Liste ist
    const existingIndex = adressen.findIndex((a) => a.adresseID === adresse.adresseID);
    if (existingIndex >= 0) {
      // Adresse aktualisieren (falls von extern aktualisiert)
      setAdressen(adressen.map((a) => (a.adresseID === adresse.adresseID ? adresse : a)));
      setSnackbar({
        open: true,
        message: `Adresse "${adresse.vollstaendigerName || adresse.firma}" ausgewählt`,
        severity: 'info',
      });
    } else {
      // Neue Adresse zur Liste hinzufügen
      setAdressen([adresse, ...adressen]);
      setSnackbar({
        open: true,
        message: `Adresse "${adresse.vollstaendigerName || adresse.firma}" hinzugefügt`,
        severity: 'success',
      });
    }
    // Dialog öffnen zum Bearbeiten
    handleOpenDialog(adresse);
  };

  // Handler wenn externe Adresse automatisch erstellt wurde
  const handleExternalAddressCreated = (adresse: Adresse) => {
    setSnackbar({
      open: true,
      message: `Externe Adresse "${adresse.vollstaendigerName || adresse.firma}" wurde automatisch erstellt`,
      severity: 'success',
    });
  };

  // Handler für Adress-Details anzeigen
  const handleOpenDetails = (adresseId: number) => {
    setSelectedAdresseId(adresseId);
    setDetailDialogOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailDialogOpen(false);
    setSelectedAdresseId(null);
  };

  const filteredAdressen = adressen.filter(
    (adresse) =>
      adresse.vollstaendigerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (adresse.firma || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (adresse.email || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4">Adressen</Typography>
        <Box display="flex" gap={2}>
          <TextField
            size="small"
            placeholder="In Tabelle filtern..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title="Aktualisieren">
            <IconButton onClick={loadAdressen} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={handleExport}
          >
            Export CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={<ImportIcon />}
            onClick={handleOpenImportDialog}
          >
            Import CSV
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Neue Adresse
          </Button>
        </Box>
      </Box>

      {/* Intelligente Adresssuche mit Web-Integration */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <WebSearchIcon color="primary" />
          <Typography variant="subtitle1" fontWeight="medium">
            Intelligente Adresssuche
          </Typography>
          <Typography variant="caption" color="text.secondary">
            (Sucht in Skeena-Datenbank + Internet)
          </Typography>
        </Box>
        <AddressSearchAutocomplete
          onAddressSelected={handleAddressSelected}
          onExternalAddressCreated={handleExternalAddressCreated}
          label="Adresse suchen oder aus dem Internet hinzufügen"
          placeholder="Name, Firma oder Adresse eingeben..."
          autoCreateExternal={true}
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name / Firma</TableCell>
              <TableCell>E-Mail</TableCell>
              <TableCell>Telefon</TableCell>
              <TableCell>Ort</TableCell>
              <TableCell align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAdressen.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="textSecondary">
                    Keine Adressen vorhanden
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredAdressen.map((adresse) => (
                <TableRow
                  key={adresse.adresseID}
                  hover
                  onClick={() => handleOpenDetails(adresse.adresseID)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {adresse.firma ? (
                        <BusinessIcon fontSize="small" color="action" />
                      ) : (
                        <PersonIcon fontSize="small" color="action" />
                      )}
                      <Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2" fontWeight="medium">
                            {adresse.vollstaendigerName}
                          </Typography>
                          {adresse.istMandant && (
                            <Chip
                              label="Mandant"
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                        {adresse.firma && (
                          <Typography variant="caption" color="textSecondary">
                            {adresse.firma}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{adresse.email || '-'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{adresse.telefon || '-'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {adresse.plz && adresse.ort
                        ? `${adresse.plz} ${adresse.ort}`
                        : adresse.ort || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <Box display="flex" justifyContent="flex-end" gap={1}>
                      <Tooltip title="Bearbeiten">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(adresse)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Löschen">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(adresse.adresseID)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Import CSV Dialog */}
      <Dialog open={importDialogOpen} onClose={handleCloseImportDialog} maxWidth="sm" fullWidth>
        <DialogTitle>CSV-Import</DialogTitle>
        <DialogContent dividers>
          <Box>
            <Typography variant="body2" color="textSecondary" mb={2}>
              Wählen Sie eine CSV-Datei zum Importieren aus. Die Datei sollte das Format aus dem Export haben (Semikolon-separiert).
            </Typography>

            <Button
              variant="outlined"
              component="label"
              fullWidth
              startIcon={<ImportIcon />}
              sx={{ mb: 2 }}
            >
              {importFile ? importFile.name : 'CSV-Datei auswählen'}
              <input
                type="file"
                hidden
                accept=".csv"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setImportFile(file);
                    setImportResult(null);
                  }
                }}
              />
            </Button>

            {importResult && (
              <Alert severity={importResult.errors > 0 ? 'warning' : 'success'} sx={{ mt: 2 }}>
                <Typography variant="body2" fontWeight="bold">
                  Import abgeschlossen
                </Typography>
                <Typography variant="body2">
                  Erfolgreich importiert: {importResult.imported}
                </Typography>
                {importResult.errors > 0 && (
                  <Typography variant="body2">
                    Fehler: {importResult.errors}
                  </Typography>
                )}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImportDialog}>
            {importResult ? 'Schließen' : 'Abbrechen'}
          </Button>
          {!importResult && (
            <Button
              variant="contained"
              onClick={handleImport}
              disabled={!importFile || importing}
              startIcon={importing ? <CircularProgress size={20} /> : <ImportIcon />}
            >
              {importing ? 'Importiere...' : 'Importieren'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog für Neue/Bearbeiten Adresse */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingAdresse ? 'Adresse bearbeiten' : 'Neue Adresse'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                label="Anrede"
                value={formData.anrede}
                onChange={(e) =>
                  setFormData({ ...formData, anrede: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                label="Titel"
                value={formData.titel}
                onChange={(e) =>
                  setFormData({ ...formData, titel: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Vorname"
                value={formData.vorname}
                onChange={(e) =>
                  setFormData({ ...formData, vorname: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Nachname"
                value={formData.nachname}
                onChange={(e) =>
                  setFormData({ ...formData, nachname: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Zusatzname"
                value={formData.zusatzName}
                onChange={(e) =>
                  setFormData({ ...formData, zusatzName: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Firma"
                value={formData.firma}
                onChange={(e) =>
                  setFormData({ ...formData, firma: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="E-Mail"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Telefon"
                value={formData.telefon}
                onChange={(e) =>
                  setFormData({ ...formData, telefon: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Straße"
                value={formData.strasse}
                onChange={(e) =>
                  setFormData({ ...formData, strasse: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Hausnummer"
                value={formData.hausnummer}
                onChange={(e) =>
                  setFormData({ ...formData, hausnummer: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Zusatzadresse"
                value={formData.zusatzadresse}
                onChange={(e) =>
                  setFormData({ ...formData, zusatzadresse: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="PLZ"
                value={formData.plz}
                onChange={(e) =>
                  setFormData({ ...formData, plz: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Postfach"
                value={formData.postfach}
                onChange={(e) =>
                  setFormData({ ...formData, postfach: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Ort"
                value={formData.ort}
                onChange={(e) =>
                  setFormData({ ...formData, ort: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Land"
                value={formData.land}
                onChange={(e) =>
                  setFormData({ ...formData, land: e.target.value })
                }
              />
            </Grid>

            {/* Mandanten-Felder */}
            <Grid item xs={12}>
              <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2, mt: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Mandanten-Status
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.istMandant || false}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      setFormData({
                        ...formData,
                        istMandant: isChecked,
                        mandantSeit: isChecked && !formData.mandantSeit
                          ? new Date().toISOString().split('T')[0]
                          : formData.mandantSeit,
                      });
                    }}
                  />
                }
                label="Ist Mandant"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Mandant seit"
                type="date"
                value={formData.mandantSeit ? formData.mandantSeit.split('T')[0] : ''}
                onChange={(e) =>
                  setFormData({ ...formData, mandantSeit: e.target.value || undefined })
                }
                disabled={!formData.istMandant}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Mandanten-Nr."
                value={formData.mandantNummer || ''}
                onChange={(e) =>
                  setFormData({ ...formData, mandantNummer: e.target.value })
                }
                disabled={!formData.istMandant}
                placeholder="z.B. M-2025-001"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Abbrechen</Button>
          <Button variant="contained" onClick={handleSave}>
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar für Benachrichtigungen */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Adresse Detail Dialog */}
      <AdresseDetailDialog
        open={detailDialogOpen}
        adresseId={selectedAdresseId}
        onClose={handleCloseDetails}
      />
    </Box>
  );
};

export default Adressen;
